/* eslint-disable no-console */
import React, { useEffect } from 'react';
import { useLocation } from '@gatsbyjs/reach-router';
import { Helmet } from 'react-helmet';
import { useReactiveVar, useLazyQuery } from '@apollo/client';
import InternalError from '../../components/InternalError';
import {
  FLIGHT_SEARCH_TYPE,
  SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS,
} from '../../utils/constants';
import { setBrowseParams } from '../../operations/localState/setSearchParams';
import { GET_BROWSE_INITIAL_INFORMATION, BROWSE_FLIGHTS_SEARCH } from '../../operations/queries';
import Dictionary from '../../utils/dictionary';
import useDictionary from '../../hooks/useDictionary';
import withResults from '../../utils/hocComponent';
import AirLoader from '../../components/AirLoader';
import BrowseResults from '../../components/BrowseResults';
import { browseParamsVar } from '../../operations/localState/reactiveVariables';
import { getSessionStorageItem } from '../../operations/sessionStorage/utils';
import setSearchResults from '../../operations/localState/setSearchResults';

const BrowseDepartureFlights = withResults(BrowseResults, FLIGHT_SEARCH_TYPE.DEPARTURE);

const BrowseResultsDeparture = () => {
  const getDictionary = useDictionary(Dictionary);
  const browseParams = useReactiveVar(browseParamsVar);
  const searchResults = getSessionStorageItem(SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS);

  const location = useLocation();
  const searchParams = new URLSearchParams(location?.search);
  const shipCode = searchParams.get('shipCode');
  const sailDate = searchParams.get('date');

  const [getInitialInformation, { loading, error, data: initialInformation }] = useLazyQuery(
    GET_BROWSE_INITIAL_INFORMATION,
  );

  const [
    getFlights,
    { loading: flightsLoading, error: flightsError, data: browseFlightsSearchData },
  ] = useLazyQuery(BROWSE_FLIGHTS_SEARCH);

  useEffect(() => {
    if (searchResults) {
      const lastDepartureFlightResults = getSessionStorageItem(
        SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS,
      );
      if (lastDepartureFlightResults && lastDepartureFlightResults.matchingFlights) {
        setSearchResults(lastDepartureFlightResults);
      }
    }
  }, []);

  useEffect(() => {
    if (shipCode && sailDate && !browseParams && !searchResults) {
      getInitialInformation({
        variables: {
          onlyActive: true,
        },
      });
      if (shipCode && sailDate) {
        // We need to send departure airport in order to get destination ariport from mathingflights results
        getFlights({
          variables: {
            numberOfGuests: 2,
            departureAirportCode: 'SFO', // default only for deeplink
            shipCode,
            sailDate,
            type: '',
          },
        });
      }
    }
  }, []);

  useEffect(() => {
    if (initialInformation) {
      const shipInfo = initialInformation?.searchSailings?.shipSailings.find(
        (sailing) => sailing.shipCode === shipCode,
      );

      const ship = {
        label: shipInfo?.shipName
          .toLowerCase()
          .replace(/(?:^|\s)(?!of|the)\S/g, (a) => a.toUpperCase()),
        code: shipInfo?.shipCode,
      };

      const searchCriteria = {
        numberOfGuests: 2,
        shipCode,
        selectedYear: sailDate.split('-')[0],
        day: sailDate.split('-')[1],
        month: sailDate.split('-')[1],
        type: '',
      };
      setBrowseParams({ ...initialInformation, searchCriteria, ship });
    }
  }, [initialInformation]);

  useEffect(() => {
    if (browseFlightsSearchData) {
      setSearchResults(
        {
          ...browseFlightsSearchData.browseFlightsSearch,
          flightType: FLIGHT_SEARCH_TYPE.DEPARTURE,
        },
        true,
      );
    }
  }, [browseFlightsSearchData]);

  useEffect(() => {
    if (document.getElementsByClassName('route-search')[0]) {
      document.getElementsByClassName('route-search')[0].className = 'route';
    }
  }, []);

  if (flightsError || error) {
    return <InternalError />;
  }

  return (
    <>
      <Helmet>
        <title>{getDictionary(Dictionary.airBrowseResultsDepartureSeoTitle.key)}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <AirLoader
        open={loading || flightsLoading}
        status={getDictionary(Dictionary.loaderRetrieveCruise.key)}
      />
      {browseParams && <BrowseDepartureFlights urlParams={shipCode && sailDate} />}
    </>
  );
};

export default BrowseResultsDeparture;
